import React, { useState, useEffect, useCallback } from 'react';
import ContentHeader from '../components/ContentHeader';
import InfoIconButton from '../components/InfoIconButton';
import { superAdminAPI } from '../services/api';
import './SuperAdmin.css';

const KNOWN_JOBS = [
  {
    code: 'credential_renewal_reminder',
    label: 'Credential renewal reminder',
    description: 'Sends an inbox notification to staff whose training window ends within N days.',
    defaultParams: { days_advance: 60, repeat_window_days: 30 },
  },
  {
    code: 'eom_checklist_reminder',
    label: 'End of Month checklist',
    description: 'Sends the EOM checklist notification to all active staff. Template configured in Tenant Settings → Notification templates.',
    defaultParams: { repeat_window_days: 25 },
  },
  {
    code: 'school_consent_check',
    label: 'School consent check',
    description: 'Checks 27+ month clients for a signed school consent document in their Intake-Consents folder. Notifies the assigned SC if missing; notifies the monitor staff (configured in Notification Templates) once when consent is found.',
    defaultParams: { repeat_window_days: 14 },
  },
  {
    code: 'delayed_need_reminder',
    label: 'Delayed service-need reminder',
    description: 'Finds service needs still unmatched 30+ days after the Date of Consent and sends one combined red notification per child to the assigned SC. Notify-only; sent once per need (delay_notified_at).',
    defaultParams: { threshold_days: 30 },
  },
];

const SCHEDULES = [
  { label: 'Daily at 8 AM',              value: '0 8 * * *'   },
  { label: 'Weekly — Mon at 8 AM',        value: '0 8 * * 1'   },
  { label: 'Monthly — 1st at 8 AM',       value: '0 8 1 * *'   },
  { label: 'Monthly — 15th at 8 AM',      value: '0 8 15 * *'  },
  { label: 'Custom…',                     value: '__custom__'   },
];

const DEFAULT_CRON = '0 8 * * *';

function isValidCron(expr) {
  if (!expr) return false;
  const fields = expr.trim().split(/\s+/);
  if (fields.length !== 5) return false;
  return fields.every((f) => /^(\*|\d{1,2}(-\d{1,2})?(,\d{1,2}(-\d{1,2})?)*|(\*|\d{1,2})\/\d{1,2})$/.test(f));
}

function scheduleLabel(expr) {
  const effective = expr || DEFAULT_CRON;
  const match = SCHEDULES.find((s) => s.value === effective);
  if (match && match.value !== '__custom__') return match.label;
  return effective;
}

const CRON_TOOLTIP = `Cron format: minute hour day-of-month month day-of-week
Each field is a number or * (any).

Examples:
  0 8 * * *    daily at 8:00 AM
  0 8 * * 1    every Monday at 8:00 AM
  0 8 1 * *    1st of every month at 8:00 AM
  0 8 15 * *   15th of every month at 8:00 AM
  0 6 * * 1-5  weekdays at 6:00 AM`;

function CronInfoIcon() {
  const [open, setOpen] = React.useState(false);
  return (
    <span className="cron-info-wrap">
      <InfoIconButton
        ariaExpanded={open}
        ariaControls="cron-hint-text"
        onClick={() => setOpen((v) => !v)}
        ariaLabel="Show cron format help"
        ariaLabelExpanded="Hide cron format help"
      />
      {open && <pre id="cron-hint-text" className="cron-hint-text">{CRON_TOOLTIP}</pre>}
    </span>
  );
}

function SchedulePicker({ value, onChange }) {
  const effective = value || DEFAULT_CRON;
  const isKnownPreset = (v) => SCHEDULES.some((s) => s.value !== '__custom__' && s.value === v);
  const [customMode, setCustomMode] = React.useState(() => !isKnownPreset(effective));
  const [customText, setCustomText] = React.useState(() => isKnownPreset(effective) ? '' : effective);

  // When parent resets the value (e.g. after save), sync local state
  React.useEffect(() => {
    if (isKnownPreset(value)) {
      setCustomMode(false);
      setCustomText('');
    } else if (value && value !== customText) {
      setCustomMode(true);
      setCustomText(value);
    }
  }, [value]); // eslint-disable-line

  const selectValue = customMode ? '__custom__' : effective;

  return (
    <div className="jobs-schedule-picker">
      <select
        value={selectValue}
        onChange={(e) => {
          if (e.target.value === '__custom__') {
            setCustomMode(true);
            setCustomText('');
          } else {
            setCustomMode(false);
            setCustomText('');
            onChange(e.target.value);
          }
        }}
      >
        {SCHEDULES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
      {customMode && (
        <>
          <div className="jobs-cron-custom-row">
            <input
              type="text"
              className={`jobs-cron-input${customText && !isValidCron(customText) ? ' jobs-cron-input--invalid' : ''}`}
              placeholder="e.g. 0 8 27 * *"
              value={customText}
              onChange={(e) => {
                setCustomText(e.target.value);
                onChange(e.target.value);
              }}
            />
            <CronInfoIcon />
          </div>
          <span className="jobs-cron-hint">minute · hour · day · month · weekday</span>
        </>
      )}
    </div>
  );
}

function jobLabel(code) {
  return KNOWN_JOBS.find((j) => j.code === code)?.label ?? code;
}

function formatDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleString();
}

export function SuperAdminJobsTab() {
  const [configs, setConfigs] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logLoading, setLogLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);
  const [editing, setEditing] = useState(null); // { tenantId, jobCode, enabled, params }
  const [logFilter, setLogFilter] = useState({ tenant_id: '', job_code: '' });
  const [addForm, setAddForm] = useState(null); // { tenantId: '', jobCode: '', cronExpr: '0 8 * * *' }
  const [allTenants, setAllTenants] = useState([]);
  const [running, setRunning] = useState(null); // "tenantId:jobCode" while in-flight
  const [dryRun, setDryRun] = useState(false);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfgRes, tenantRes] = await Promise.all([
        superAdminAPI.listJobConfigs(),
        superAdminAPI.getAllTenants(),
      ]);
      setConfigs(cfgRes.data.data ?? []);
      setAllTenants(tenantRes.data.data ?? []);
    } catch (err) {
      setError(err.message || 'Failed to load job configs');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = {};
      if (logFilter.tenant_id) params.tenant_id = logFilter.tenant_id;
      if (logFilter.job_code) params.job_code = logFilter.job_code;
      const res = await superAdminAPI.listJobLog(params);
      setLog(res.data.data ?? []);
    } catch (err) {
      setLog([]);
    } finally {
      setLogLoading(false);
    }
  }, [logFilter]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);
  useEffect(() => { loadLog(); }, [loadLog]);

  async function saveEditing() {
    if (!editing) return;
    if (!isValidCron(editing.cronExpr)) { setSaveMsg('Error: invalid cron expression'); return; }
    setSaveMsg(null);
    try {
      let parsedParams = editing.params;
      if (typeof parsedParams === 'string') {
        parsedParams = JSON.parse(parsedParams);
      }
      await superAdminAPI.upsertJobConfig(editing.tenantId, editing.jobCode, {
        enabled: editing.enabled,
        params: parsedParams,
        cron_expression: editing.cronExpr,
      });
      setSaveMsg('Saved.');
      setEditing(null);
      await loadConfigs();
    } catch (err) {
      setSaveMsg('Error: ' + (err.message || 'save failed'));
    }
  }

  async function runNow(tenantId, jobCode) {
    const key = `${tenantId}:${jobCode}`;
    setRunning(key);
    setSaveMsg(null);
    try {
      const res = await superAdminAPI.runJob({ job_code: jobCode, tenant_id: tenantId, dry_run: dryRun });
      const results = res.data.results ?? [];
      const r = results[0];
      const prefix = dryRun ? 'DRY RUN — ' : '';
      if (r?.skipped) {
        setSaveMsg(`${prefix}Skipped: ${r.reason}`);
      } else if (r?.error) {
        setSaveMsg(`${prefix}Error: ${r.error}`);
      } else {
        setSaveMsg(`${prefix}sent: ${r?.result?.sent ?? 0}, skipped: ${r?.result?.skipped ?? 0}`);
      }
      await Promise.all([loadConfigs(), loadLog()]);
    } catch (err) {
      setSaveMsg('Error: ' + (err.message || 'run failed'));
    } finally {
      setRunning(null);
    }
  }

  async function saveAdd() {
    if (!addForm) return;
    if (!isValidCron(addForm.cronExpr || DEFAULT_CRON)) { setSaveMsg('Error: invalid cron expression'); return; }
    setSaveMsg(null);
    const jobDef = KNOWN_JOBS.find((j) => j.code === addForm.jobCode);
    try {
      await superAdminAPI.upsertJobConfig(addForm.tenantId, addForm.jobCode, {
        enabled: false,
        params: jobDef?.defaultParams ?? {},
        cron_expression: addForm.cronExpr || '0 8 * * *',
      });
      setSaveMsg('Config added.');
      setAddForm(null);
      await loadConfigs();
    } catch (err) {
      setSaveMsg('Error: ' + (err.message || 'add failed'));
    }
  }

  const tenantsWithConfig = new Set(configs.map((c) => `${c.tenant_id}:${c.job_code}`));

  return (
    <div className="super-admin-tab-content">
      <ContentHeader title="Scheduled Jobs" />

      {error && <p className="error-text">{error}</p>}
      {saveMsg && <p className="save-message" style={{ color: saveMsg.startsWith('Error') ? '#dc3545' : '#28a745' }}>{saveMsg}</p>}

      <section className="super-admin-section">
        <div className="super-admin-section-header">
          <h2>Job configuration</h2>
          <div className="jobs-header-actions">
            <label className="jobs-dry-run-label">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Dry run
            </label>
            <button
              type="button"
              className="btn-sm"
              onClick={() => { setAddForm({ tenantId: '', jobCode: KNOWN_JOBS[0]?.code ?? '', cronExpr: '0 8 * * *' }); setSaveMsg(null); }}
            >
              + Add config
            </button>
          </div>
        </div>

        {addForm && (
          <div className="jobs-add-form">
            <label>
              Tenant
              <select value={addForm.tenantId} onChange={(e) => setAddForm((f) => ({ ...f, tenantId: e.target.value }))}>
                <option value="">— select —</option>
                {allTenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.code})</option>
                ))}
              </select>
            </label>
            <label>
              Job
              <select value={addForm.jobCode} onChange={(e) => setAddForm((f) => ({ ...f, jobCode: e.target.value }))}>
                {KNOWN_JOBS.map((j) => <option key={j.code} value={j.code}>{j.label}</option>)}
              </select>
            </label>
            <SchedulePicker
              value={addForm.cronExpr ?? '0 8 * * *'}
              onChange={(v) => setAddForm((f) => ({ ...f, cronExpr: v }))}
            />
            <div className="jobs-add-actions">
              <button type="button" className="btn-sm btn-primary" onClick={saveAdd} disabled={!addForm.tenantId || !addForm.jobCode}>Add</button>
              <button type="button" className="btn-sm" onClick={() => setAddForm(null)}>Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <p>Loading…</p>
        ) : configs.length === 0 ? (
          <p className="hint">No job configs yet. Use "+ Add config" to enable a job for a tenant.</p>
        ) : (
          <div className="super-admin-table">
            <table>
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Job</th>
                  <th>Enabled</th>
                  <th><span className="jobs-th-with-icon">Schedule <CronInfoIcon /></span></th>
                  <th>Params</th>
                  <th>Last ran</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {configs.map((cfg) => {
                  const isEditing = editing?.tenantId === cfg.tenant_id && editing?.jobCode === cfg.job_code;
                  return (
                    <tr key={`${cfg.tenant_id}:${cfg.job_code}`}>
                      <td>{cfg.tenant_code}</td>
                      <td>{jobLabel(cfg.job_code)}</td>
                      <td>
                        {isEditing ? (
                          <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing((prev) => ({ ...prev, enabled: e.target.checked }))} />
                        ) : (
                          <span className={cfg.enabled ? 'status-enabled' : 'status-disabled'}>{cfg.enabled ? 'Yes' : 'No'}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <SchedulePicker
                            value={editing.cronExpr ?? '0 8 * * *'}
                            onChange={(v) => setEditing((prev) => ({ ...prev, cronExpr: v }))}
                          />
                        ) : (
                          <span className="jobs-schedule-label">{scheduleLabel(cfg.cron_expression)}</span>
                        )}
                      </td>
                      <td className="jobs-params-cell">
                        {isEditing ? (
                          <textarea
                            className="jobs-params-textarea"
                            value={typeof editing.params === 'string' ? editing.params : JSON.stringify(editing.params, null, 2)}
                            onChange={(e) => setEditing((prev) => ({ ...prev, params: e.target.value }))}
                            rows={4}
                          />
                        ) : (
                          <code className="jobs-params-code">{JSON.stringify(cfg.params)}</code>
                        )}
                      </td>
                      <td>{formatDate(cfg.last_ran_at)}</td>
                      <td>
                        {isEditing ? (
                          <span className="table-actions">
                            <button type="button" className="btn-sm btn-primary" onClick={saveEditing} disabled={!isValidCron(editing.cronExpr)}>Save</button>
                            <button type="button" className="btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                          </span>
                        ) : (
                            <span className="table-actions">
                            <button
                              type="button"
                              className="btn-sm"
                              onClick={() => {
                                setEditing({ tenantId: cfg.tenant_id, jobCode: cfg.job_code, enabled: cfg.enabled, params: JSON.stringify(cfg.params, null, 2), cronExpr: cfg.cron_expression ?? '0 8 * * *' });
                                setSaveMsg(null);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn-sm btn-primary"
                              disabled={running === `${cfg.tenant_id}:${cfg.job_code}`}
                              onClick={() => runNow(cfg.tenant_id, cfg.job_code)}
                            >
                              {running === `${cfg.tenant_id}:${cfg.job_code}` ? 'Running…' : dryRun ? 'Run dry' : 'Run now'}
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="super-admin-section">
        <div className="super-admin-section-header">
          <h2>Job run log</h2>
          <div className="jobs-log-filters">
            <select value={logFilter.tenant_id} onChange={(e) => setLogFilter((f) => ({ ...f, tenant_id: e.target.value }))}>
              <option value="">All tenants</option>
              {allTenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select value={logFilter.job_code} onChange={(e) => setLogFilter((f) => ({ ...f, job_code: e.target.value }))}>
              <option value="">All jobs</option>
              {KNOWN_JOBS.map((j) => <option key={j.code} value={j.code}>{j.label}</option>)}
            </select>
          </div>
        </div>

        {logLoading ? (
          <p>Loading…</p>
        ) : log.length === 0 ? (
          <p className="hint">No log entries found.</p>
        ) : (
          <div className="super-admin-table">
            <table>
              <thead>
                <tr>
                  <th>Ran at</th>
                  <th>Tenant</th>
                  <th>Job</th>
                  <th>Target</th>
                  <th>Triggered by</th>
                  <th>Status</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {log.map((entry) => (
                  <tr key={entry.id}>
                    <td className="jobs-log-date">{formatDate(entry.ran_at)}</td>
                    <td>{entry.tenant_code}</td>
                    <td>{jobLabel(entry.job_code)}</td>
                    <td>
                      {entry.target_type === 'staff' && (entry.target_last_name || entry.target_first_name)
                        ? `${entry.target_last_name}, ${entry.target_first_name}`.replace(/^, |, $/, '')
                        : entry.target_type === 'client' && (entry.target_client_last || entry.target_client_first)
                        ? `${entry.target_client_last}, ${entry.target_client_first}`.replace(/^, |, $/, '')
                        : entry.target_type ? `${entry.target_type}` : '—'}
                    </td>
                    <td>{entry.triggered_by}</td>
                    <td><span className={`job-status-${entry.status}`}>{entry.status}</span></td>
                    <td>{entry.detail || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
